import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatearFechaDisplay } from "../../../resources/Helper";

const formatearMoneda = (valor) =>
    "$" + Number(valor || 0).toLocaleString("es-CO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatearFecha = (valor) => formatearFechaDisplay(valor, "—");

/** Paleta amigable con impresión en escala de grises */
const LIQ_HEADER_BG = "#e5e7eb";
const LIQ_HEADER_TEXT = "#1f2937";
const LIQ_HEADER_BORDER = "#d1d5db";
const LIQ_SALDO_HEADER_BG = "#eaeaea";
const LIQ_SALDO_VALUE_BG = "#f3f4f6";

const formatearFechaHora = (valor) => {
    if (!valor) return formatearFechaDisplay(new Date(), "—");
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return formatearFecha(valor);
    return d.toLocaleString("es-CO");
};

const esFacturaNoElectronica = (f) =>
    f.tipo_factura === "no_electronica" || [4, 5].includes(Number(f.estado));

const labelTipoFactura = (f) =>
    esFacturaNoElectronica(f) ? "No electrónica" : "Electrónica";

const esc = (val) =>
    String(val ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/** Datos listos para el modal / vista de impresión. */
export function buildLiquidacionHonorariosView(data, client) {
    const pct = data.porcentaje_contratado ?? 0;
    const valorBruto = Number(data.valor_bruto) || 0;
    const valorLiquidacion = Number(data.valor_liquidacion) || 0;
    const ajusteVal = Number(data.ajuste) || 0;
    const valorTotalNum = Number(data.valor_total) || valorLiquidacion + ajusteVal;
    const nombreClinica = client?.nombre_comercial || client?.nombre || "";
    const clinicaVisible = nombreClinica && !/^cliente\s*\d+$/i.test(nombreClinica.trim());
    const esAnulada = data.estado === "anulada";

    const filasFacturas = (data.facturas || []).map((f, i) => ({
        key: f.id || i,
        numero: f.numero || String(f.id),
        tipo: labelTipoFactura(f),
        paciente: f.paciente_nombre || "—",
        fecha: formatearFecha(f.fecha_emision),
        valor: formatearMoneda(f.valor_bruto),
    }));

    return {
        esAnulada,
        fechaEmision: formatearFechaHora(data.created_at),
        profesional: data.profesional_nombre || data.profesional?.nombre || "—",
        periodo: `${formatearFecha(data.fecha_inicio)} al ${formatearFecha(data.fecha_fin)}`,
        clinicaVisible,
        nombreClinica,
        pct,
        facturasCount: (data.facturas || []).length,
        fechaAnulacion: data.fecha_anulacion ? formatearFecha(data.fecha_anulacion) : null,
        motivoAnulacion: data.motivo_anulacion || null,
        valorBruto: formatearMoneda(valorBruto),
        valorLiquidacion: formatearMoneda(valorLiquidacion),
        ajusteVal: formatearMoneda(ajusteVal),
        valorTotal: formatearMoneda(valorTotalNum),
        filasFacturas,
    };
}

export function getLiquidacionHonorariosStyles() {
    return `
* { box-sizing: border-box; }
body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #1f2937;
    margin: 0;
    padding: 20px 24px;
    background: #fff;
}
.lh-document {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #1f2937;
    padding: 20px 24px;
    background: #fff;
}
.lh-report-title {
    background: ${LIQ_HEADER_BG};
    color: ${LIQ_HEADER_TEXT};
    border: 1px solid ${LIQ_HEADER_BORDER};
    font-weight: 800;
    font-size: 1.15rem;
    letter-spacing: 0.04em;
    text-align: center;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 8px;
}
.lh-fecha-emision {
    text-align: right;
    font-size: 0.82rem;
    color: #334155;
    margin-bottom: 12px;
}
.lh-anulada-banner {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    padding: 8px 12px;
    border-radius: 8px;
    margin-bottom: 12px;
    font-weight: 700;
    text-align: center;
}
.lh-info-card {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
    margin-bottom: 14px;
}
.lh-info-grid {
    display: table;
    width: 100%;
}
.lh-info-col {
    display: table-cell;
    width: 50%;
    padding: 14px 16px;
    vertical-align: top;
}
.lh-info-col + .lh-info-col { border-left: 1px solid #e5e7eb; }
.lh-info-col p { margin: 0 0 8px; font-size: 0.88rem; }
.lh-info-col p:last-child { margin-bottom: 0; }
.lh-saldo-box {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;
    text-align: center;
}
.lh-saldo-box__header {
    background: ${LIQ_SALDO_HEADER_BG};
    color: ${LIQ_HEADER_TEXT};
    border-bottom: 1px solid ${LIQ_HEADER_BORDER};
    font-weight: 700;
    padding: 9px 14px;
    font-size: 0.95rem;
}
.lh-saldo-box__value {
    background: ${LIQ_SALDO_VALUE_BG};
    color: #111827;
    font-weight: 800;
    font-size: 1.25rem;
    padding: 12px 14px;
}
.lh-section-title {
    font-weight: 800;
    font-size: 0.95rem;
    letter-spacing: 0.04em;
    color: #1f2937;
    margin: 0 0 8px;
}
.lh-table-wrap {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 18px;
}
table.lh-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}
table.lh-table th {
    background: #eaeaea;
    color: #1f2937;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
}
table.lh-table td {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    vertical-align: middle;
    font-size: 12px;
}
table.lh-table tbody tr:nth-child(even) td { background: #fafafa; }
.lh-resumen-card {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    overflow: hidden;
    width: 100%;
    margin-bottom: 8px;
}
table.lh-resumen {
    width: 100%;
    border-collapse: collapse;
}
table.lh-resumen td {
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    font-size: 12px;
}
table.lh-resumen .label {
    color: #4b5563;
    text-align: right;
    width: 70%;
    background: #fafafa;
}
table.lh-resumen .value {
    text-align: right;
    font-weight: 600;
    color: #111827;
}
table.lh-resumen tr.total-final td {
    background: #fafafa;
    color: #111827;
    font-weight: 800;
    font-size: 13px;
}
.text-center { text-align: center; }
.text-right { text-align: right; }
`;
}

export function buildLiquidacionHonorariosBodyHtml(view) {
    const filasFacturas = (view.filasFacturas || []).map((f) => `
        <tr>
            <td class="text-center">${esc(f.numero)}</td>
            <td class="text-center">${esc(f.tipo)}</td>
            <td>${esc(f.paciente)}</td>
            <td class="text-center">${esc(f.fecha)}</td>
            <td class="text-right">${esc(f.valor)}</td>
        </tr>
    `).join("");

    return `
<div class="lh-document">
    <div class="lh-report-title">LIQUIDACIÓN DE HONORARIOS${view.esAnulada ? " — ANULADA" : ""}</div>
    <div class="lh-fecha-emision">Fecha de emisión: ${esc(view.fechaEmision)}</div>
    ${view.esAnulada ? '<div class="lh-anulada-banner">LIQUIDACIÓN ANULADA — Solo referencia histórica</div>' : ""}
    <div class="lh-info-card">
        <div class="lh-info-grid">
            <div class="lh-info-col">
                <p><strong>Profesional:</strong> ${esc(view.profesional)}</p>
                <p><strong>Período:</strong> ${esc(view.periodo)}</p>
                ${view.clinicaVisible ? `<p><strong>Clínica:</strong> ${esc(view.nombreClinica)}</p>` : ""}
            </div>
            <div class="lh-info-col">
                <p><strong>Porcentaje pactado:</strong> ${view.pct}%</p>
                <p><strong>Facturas incluidas:</strong> ${view.facturasCount}</p>
                ${view.esAnulada && view.fechaAnulacion ? `<p><strong>Fecha anulación:</strong> ${esc(view.fechaAnulacion)}</p>` : ""}
                ${view.esAnulada && view.motivoAnulacion ? `<p><strong>Motivo anulación:</strong> ${esc(view.motivoAnulacion)}</p>` : ""}
            </div>
        </div>
    </div>
    <div class="lh-saldo-box">
        <div class="lh-saldo-box__header">TOTAL A PAGAR</div>
        <div class="lh-saldo-box__value">${esc(view.valorTotal)}</div>
    </div>
    <div class="lh-section-title">FACTURAS DEL PERÍODO</div>
    <div class="lh-table-wrap">
        <table class="lh-table">
            <thead>
                <tr>
                    <th class="text-center">N° Factura</th>
                    <th class="text-center">Tipo</th>
                    <th>Paciente</th>
                    <th class="text-center">Fecha</th>
                    <th class="text-right">Valor bruto</th>
                </tr>
            </thead>
            <tbody>
                ${filasFacturas || '<tr><td colspan="5" class="text-center" style="color:#6b7280;padding:16px;">Sin facturas en el periodo</td></tr>'}
            </tbody>
        </table>
    </div>
    <div class="lh-section-title">RESUMEN DE LIQUIDACIÓN</div>
    <div class="lh-resumen-card">
        <table class="lh-resumen">
            <tbody>
                <tr><td class="label">Valor bruto facturado</td><td class="value">${esc(view.valorBruto)}</td></tr>
                <tr><td class="label">Liquidación base (${view.pct}%)</td><td class="value">${esc(view.valorLiquidacion)}</td></tr>
                <tr><td class="label">Ajuste</td><td class="value">${esc(view.ajusteVal)}</td></tr>
                <tr class="total-final"><td class="label">TOTAL A PAGAR</td><td class="value">${esc(view.valorTotal)}</td></tr>
            </tbody>
        </table>
    </div>
</div>`;
}

export function buildLiquidacionHonorariosPrintHtml(view) {
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Liquidación de honorarios</title>
<style>${getLiquidacionHonorariosStyles()}</style>
</head><body>
${buildLiquidacionHonorariosBodyHtml(view)}
</body></html>`;
}

/** Imprime solo el documento de liquidación (iframe aislado, sin la página de fondo). */
export function printLiquidacionHonorarios(view) {
    const html = buildLiquidacionHonorariosPrintHtml(view);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Imprimir liquidación");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:none;";
    document.body.appendChild(iframe);

    const frameDoc = iframe.contentWindow?.document;
    if (!frameDoc) {
        document.body.removeChild(iframe);
        alert("No se pudo preparar la impresión.");
        return;
    }

    frameDoc.open();
    frameDoc.write(html);
    frameDoc.close();

    const imprimir = () => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } catch (e) {
            alert("No se pudo abrir el diálogo de impresión.");
        }
        setTimeout(() => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 1500);
    };

    if (iframe.contentWindow.document.readyState === "complete") {
        setTimeout(imprimir, 250);
    } else {
        iframe.onload = () => setTimeout(imprimir, 250);
    }
}

/** Descarga PDF con estilo similar a estado de cuenta. */
export function downloadLiquidacionHonorariosPdf(data, client) {
    const view = buildLiquidacionHonorariosView(data, client);
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margen = 14;
    const ancho = pageWidth - margen * 2;
    let y = 12;
    const grisClaro = [229, 231, 235];
    const grisMedio = [234, 234, 234];
    const grisSaldo = [243, 244, 246];
    const textoOscuro = [31, 41, 55];
    const nombreEmpresa = client?.nombre || client?.nombre_comercial || "SmileWEB";

    const asegurarEspacio = (needed = 18) => {
        if (y + needed > pageHeight - 18) {
            doc.addPage();
            y = 16;
        }
    };

    const tituloSeccion = (texto) => {
        asegurarEspacio(12);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(33, 37, 41);
        doc.text(texto, margen, y);
        y += 5;
    };

    const estilosTabla = {
        theme: "grid",
        margin: { left: margen, right: margen },
        styles: {
            halign: "center",
            fontSize: 8.5,
            cellPadding: 2,
            lineColor: [220, 224, 229],
            lineWidth: 0.2,
            textColor: [33, 37, 41],
        },
        headStyles: {
            fillColor: grisMedio,
            textColor: [31, 41, 55],
            fontStyle: "bold",
            lineColor: [209, 213, 219],
        },
    };

    const piePagina = () => {
        const total = doc.getNumberOfPages();
        for (let i = 1; i <= total; i += 1) {
            doc.setPage(i);
            doc.setDrawColor(210, 214, 220);
            doc.setLineWidth(0.3);
            doc.line(margen, pageHeight - 12, pageWidth - margen, pageHeight - 12);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text(String(i), pageWidth / 2, pageHeight - 7, { align: "center" });
            doc.text(`Generado por ${nombreEmpresa}`, pageWidth - margen, pageHeight - 7, { align: "right" });
        }
    };

    const tituloDoc = view.esAnulada ? "LIQUIDACIÓN DE HONORARIOS — ANULADA" : "LIQUIDACIÓN DE HONORARIOS";
    doc.setFillColor(...grisClaro);
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.3);
    doc.roundedRect(margen, y, ancho, 10, 2, 2, "FD");
    doc.setTextColor(...textoOscuro);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(tituloDoc, pageWidth / 2, y + 6.8, { align: "center" });
    y += 15;

    doc.setTextColor(33, 37, 41);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Fecha de emisión: ${view.fechaEmision}`, pageWidth - margen, y, { align: "right" });
    y += 6;

    if (view.esAnulada) {
        doc.setFillColor(254, 242, 242);
        doc.setDrawColor(254, 202, 202);
        doc.roundedRect(margen, y, ancho, 8, 2, 2, "FD");
        doc.setTextColor(185, 28, 28);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("LIQUIDACIÓN ANULADA — Solo referencia histórica", pageWidth / 2, y + 5.2, { align: "center" });
        doc.setTextColor(33, 37, 41);
        y += 12;
    }

    const cajaH = 28;
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.4);
    doc.roundedRect(margen, y, ancho, cajaH, 2.5, 2.5, "S");
    doc.line(margen + ancho / 2, y + 3, margen + ancho / 2, y + cajaH - 3);

    const col1 = margen + 4;
    const col2 = margen + ancho / 2 + 4;
    const lineaDato = (x, yy, label, valor) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(`${label}:`, x, yy);
        const lw = doc.getTextWidth(`${label}: `);
        doc.setFont("helvetica", "normal");
        doc.text(String(valor || "—"), x + lw + 1, yy);
    };
    lineaDato(col1, y + 7, "Profesional", view.profesional);
    lineaDato(col1, y + 14, "Período", view.periodo);
    if (view.clinicaVisible) lineaDato(col1, y + 21, "Clínica", view.nombreClinica);
    lineaDato(col2, y + 7, "Porcentaje pactado", `${view.pct}%`);
    lineaDato(col2, y + 14, "Facturas incluidas", view.facturasCount);
    if (view.esAnulada && view.fechaAnulacion) lineaDato(col2, y + 21, "Fecha anulación", view.fechaAnulacion);
    y += cajaH + 8;

    asegurarEspacio(14);
    doc.setFillColor(...grisSaldo);
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.3);
    doc.roundedRect(margen, y, ancho, 9, 4.5, 4.5, "FD");
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`TOTAL A PAGAR: ${view.valorTotal}`, pageWidth / 2, y + 6.1, { align: "center" });
    doc.setTextColor(33, 37, 41);
    y += 16;

    tituloSeccion("FACTURAS DEL PERÍODO");
    const bodyFacturas = view.filasFacturas.length
        ? view.filasFacturas.map((f) => [f.numero, f.tipo, f.paciente, f.fecha, f.valor])
        : [["—", "—", "Sin facturas en el periodo", "—", "—"]];

    autoTable(doc, {
        startY: y,
        ...estilosTabla,
        head: [["N° Factura", "Tipo", "Paciente", "Fecha", "Valor bruto"]],
        body: bodyFacturas,
        columnStyles: {
            0: { cellWidth: 32 },
            1: { cellWidth: 28 },
            2: { halign: "left" },
            3: { cellWidth: 24 },
            4: { halign: "right", cellWidth: 28 },
        },
    });
    y = (doc.lastAutoTable?.finalY || y) + 8;

    tituloSeccion("RESUMEN DE LIQUIDACIÓN");
    autoTable(doc, {
        startY: y,
        ...estilosTabla,
        head: [["Concepto", "Valor"]],
        body: [
            ["Valor bruto facturado", view.valorBruto],
            [`Liquidación base (${view.pct}%)`, view.valorLiquidacion],
            ["Ajuste", view.ajusteVal],
            ["TOTAL A PAGAR", view.valorTotal],
        ],
        columnStyles: {
            0: { halign: "right" },
            1: { halign: "right" },
        },
        didParseCell: (cellData) => {
            if (cellData.section === "body" && cellData.row.index === 3) {
                cellData.cell.styles.fontStyle = "bold";
            }
        },
    });

    piePagina();

    const slugProf = String(view.profesional || "profesional")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase()
        .slice(0, 40);
    doc.save(`liquidacion-honorarios-${slugProf || "reporte"}.pdf`);
}

export { formatearMoneda, formatearFecha };
